import { ReactNode, useEffect } from "react";
import { cn } from "../../lib/cn";
import { Button } from "./Button";

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children?: ReactNode;
  footer?: ReactNode;
  size?: "sm" | "md" | "lg";
}

const sizeClasses = {
  sm: "max-w-md",
  md: "max-w-lg",
  lg: "max-w-2xl",
};

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = "md",
}: ModalProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center p-4 sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <button
        type="button"
        className="absolute inset-0 bg-content-primary/40 transition-opacity"
        onClick={onClose}
        aria-label="Close modal"
      />
      <div
        className={cn(
          "relative z-10 w-full rounded-xl border border-border bg-surface shadow-elevated",
          "max-h-[90vh] overflow-y-auto",
          sizeClasses[size]
        )}
      >
        <div className="border-b border-brand-100 bg-brand-50/50 px-6 py-4">
          <h2 id="modal-title" className="text-lg font-semibold text-brand-800">
            {title}
          </h2>
          {description && (
            <p className="mt-1 text-sm text-content-muted">{description}</p>
          )}
        </div>
        {children && <div className="px-6 py-4">{children}</div>}
        {footer ?? (
          <div className="flex justify-end gap-2 border-t border-border px-6 py-4">
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export function ModalFooter({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex flex-col-reverse gap-2 border-t border-border px-6 py-4 sm:flex-row sm:justify-end",
        className
      )}
    >
      {children}
    </div>
  );
}
